'use client';
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

const AUTHORIZED_EMAIL = 'emailajefferson@gmail.com';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthorized: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAuthorized: false,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Track whether the initial session check is done — never set loading=false until this is true
  const initialCheckDone = useRef(false);

  const isAuthorized = user?.email?.toLowerCase() === AUTHORIZED_EMAIL.toLowerCase();

  useEffect(() => {
    // Hard timeout — loading can NEVER hang forever (8s to give getSession time)
    const timeout = setTimeout(() => {
      console.warn('[AuthContext] Hard timeout — forcing loading=false');
      initialCheckDone.current = true;
      setLoading(false);
    }, 8000);

    // Subscribe to auth state changes FIRST before calling getSession
    // This ensures we don't miss any auth events
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      console.log('[AuthContext] onAuthStateChange:', _event, newSession?.user?.email ?? 'no user');
      setSession(newSession);
      setUser(newSession?.user ?? null);
      // Only mark loading done after initial check is complete
      if (initialCheckDone.current) {
        setLoading(false);
      }
    });

    // Get initial session — this is the authoritative source of truth
    supabase.auth
      .getSession()
      .then(({ data: { session: initialSession } }) => {
        clearTimeout(timeout);
        console.log('[AuthContext] getSession resolved:', initialSession?.user?.email ?? 'no session');
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        initialCheckDone.current = true;
        setLoading(false);
      })
      .catch((err) => {
        console.error('[AuthContext] getSession error:', err);
        clearTimeout(timeout);
        initialCheckDone.current = true;
        setLoading(false);
      });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    if (email.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
      return { error: 'Access denied. This is a private system.' };
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signUp = async (_email: string, _password: string, _fullName: string) => {
    return { error: 'Registration is disabled. This is a private system.' };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    try { localStorage.clear(); } catch (_) {}
    try { sessionStorage.clear(); } catch (_) {}
    window.location.replace('/sign-up-login-screen');
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAuthorized, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
